'use client';
import { OrderLoyaltyReward } from '@eliadmin/core/src/components/loyalty/order-loyalty-reward';

import { useId } from 'react';

import type { CustomerOrder } from '@eliadmin/core/src/api/customers/customers-types';
import { orderDetailHref } from '@eliadmin/core/src/components/dlz/account/account-pages';
import {
  CancelledNote,
  ICON_STROKE,
  OrderItemImage,
  OrderStatusPill,
  ReorderMessage,
  TEXT_ACTION,
} from '@eliadmin/core/src/components/dlz/account/orders/order-parts';
import {
  orderStage,
  stageFilter,
} from '@eliadmin/core/src/components/dlz/account/orders/order-stage';
import { ORDERS_COPY } from '@eliadmin/core/src/components/dlz/account/orders/orders-copy';
import {
  orderDate,
  orderNumber,
  orderTotal,
  usePrice,
} from '@eliadmin/core/src/components/dlz/account/orders/orders-data';
import { useOrderDocument } from '@eliadmin/core/src/components/dlz/account/orders/use-order-document';
import { useReorder } from '@eliadmin/core/src/components/dlz/account/orders/use-reorder';
import { productCount } from '@eliadmin/core/src/components/dlz/checkout/cart-math';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import Link from '@eliadmin/core/src/components/link';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/** At most four tiles fit the 343 px phone card: more lines show three images and „+N“. */
const MAX_THUMBS = 4;

/** Figma „Thumbs“ (60323:20297): 56 px product images 8 px apart. */
function Thumbs({ items }: { items: CustomerOrder['items'] }) {
  const shown = items.length > MAX_THUMBS ? items.slice(0, MAX_THUMBS - 1) : items;
  const rest = items.length - shown.length;

  return (
    <ul className="flex gap-2">
      {shown.map(item => (
        <li key={item.id}>
          <OrderItemImage url={item.image?.url} alt={item.name} size={56} />
        </li>
      ))}
      {rest ? (
        <li className="dlz-body-s-medium flex size-14 items-center justify-center rounded-[10px] bg-[#F5F6F7] text-[#4F545C]">
          <span aria-hidden="true">{ORDERS_COPY.moreProducts(rest)}</span>
          <span className="sr-only">{ORDERS_COPY.moreProductsLabel(rest)}</span>
        </li>
      ) : null}
    </ul>
  );
}

/**
 * Figma „Order · …“ (desktop 60323:20284, phones 60459:90866): number and date, the status pill,
 * the total with the product count, the product images and, under a divider, „Detail objednávky“,
 * the invoice, „Kúpiť znova“ (and for a cancelled order the cancellation line). An order in
 * progress has the 2 px border/warning frame. From a 30rem column the header and the actions are
 * rows; narrower the pill and total share a row and the detail button is full width above the text
 * actions. „Sledovať zásielku“ of the design is left out – the API has no tracking link.
 */
export function OrderCard({ order }: { order: CustomerOrder }) {
  const { t } = useTranslations();
  const price = usePrice();
  const titleId = useId();
  const number = orderNumber(order);
  const stage = orderStage(order);
  const cancelled = stage === 'cancelled';
  const { document, download, loading } = useOrderDocument(order.documents);
  const reorder = useReorder(order);

  return (
    <article
      aria-labelledby={titleId}
      className={cn(
        'flex flex-col gap-4 rounded-[18px] bg-white p-4 md:p-6',
        stageFilter(stage) === 'active' ? 'border-2 border-[#F6D592]' : 'border border-[#E2E4E7]',
      )}
    >
      <header className="flex flex-col gap-2 @[30rem]:flex-row @[30rem]:items-center @[30rem]:gap-4">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h2 id={titleId} className="dlz-h5 text-[#0F1012]">
            {ORDERS_COPY.order(number)}
          </h2>
          <p className="dlz-caption text-[#6B7079]">
            {ORDERS_COPY.created(orderDate(order.created_at))}
          </p>
        </div>
        <div className="flex items-center gap-4 @[30rem]:contents">
          <OrderStatusPill name={order.status.name} stage={stage} />
          <p
            className={cn(
              'ml-auto flex flex-col items-end whitespace-nowrap text-right',
              cancelled ? 'text-[#6B7079]' : 'text-[#0F1012]',
            )}
          >
            <span className="dlz-h5">{price(orderTotal(order))}</span>
            <span className="dlz-caption text-[#6B7079]">
              {productCount(order.items.length, t)}
            </span>
          </p>
        </div>
      </header>

      <Thumbs items={order.items} />

      <div className="flex flex-col gap-4 border-t border-[#E2E4E7] pt-4 @[30rem]:flex-row @[30rem]:items-center">
        <Button asChild variant="secondary" size="m" className="w-full @[30rem]:w-auto">
          <Link href={orderDetailHref(number)} prefetch={false} aria-describedby={titleId}>
            {t('text17')}
          </Link>
        </Button>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 @[30rem]:flex-1 @[30rem]:gap-x-4">
          {document ? (
            <button
              type="button"
              onClick={download}
              disabled={loading}
              aria-busy={loading}
              aria-describedby={titleId}
              className={TEXT_ACTION}
            >
              {ORDERS_COPY.document(document.name || ORDERS_COPY.documentFallback)}
            </button>
          ) : null}
          {cancelled ? (
            <CancelledNote status={order.status.name} className="@[30rem]:ml-2" />
          ) : null}
          {reorder.available ? (
            <button
              type="button"
              onClick={reorder.reorder}
              disabled={reorder.disabled}
              aria-busy={reorder.pending}
              aria-describedby={titleId}
              className={cn(TEXT_ACTION, 'inline-flex items-center gap-1.5 @[30rem]:ml-auto')}
            >
              {reorder.pending ? (
                <Spinner className="size-[18px]" />
              ) : (
                <Icon name="cart" size={18} className={ICON_STROKE} />
              )}
              {ORDERS_COPY.reorder}
            </button>
          ) : null}
        </div>
      </div>
      <ReorderMessage result={reorder.result} />

      <OrderLoyaltyReward reward={order.loyalty_reward} compact className="w-full" />
    </article>
  );
}
